import { useEffect, useMemo } from 'react';
import { DemeterCommodity } from '../../../../../Generated/Raven-Demeter';
import { useApplicationDispatch, useApplicationSelector } from '../../../../../Redux/ReduxStore';
import { selectValueMatrixTableInputsRequest, setValueMatrixTableInputsRequest } from '../../../../../Redux/Slices/ValueMatrixCalculatorSlice';
import useSymbolsApi from '../../../../Apis/Hooks/useSymbolsApiHook';
import Dropdown from '../../../../Components/Form/Inputs/Dropdown';
import formattingService from '../../../../Services/Formatting/FormattingService';
import useLanguage from '../../../../Services/Language/useLanguageHook';
import styles from '../ValueMatrixCalculator.module.scss';

interface IValueMatrixProductDropdownProps {
    refreshGetValueMatrixCalculator: () => void;
}

const notAllowedCommodities: DemeterCommodity[] = [DemeterCommodity.FluidMilk];

const ValueMatrixProductDropdown: React.FC<IValueMatrixProductDropdownProps> = (props: IValueMatrixProductDropdownProps) => {
    const [translations] = useLanguage();
    const symbols = useSymbolsApi();
    const dispatch = useApplicationDispatch();
    const valueMatrixTableInputs = useApplicationSelector(selectValueMatrixTableInputsRequest);

    const productOptions = useMemo(() => {
        if (!symbols) {
            return [];
        }

        return symbols
            ?.filter((x) => x.symbolCategory === valueMatrixTableInputs.market)
            .filter((x) => !notAllowedCommodities.includes(x.commodity as DemeterCommodity))
            .sort((a, b) => `${a.subExchange ?? a.exchange}-${a.displayName}`.localeCompare(`${b.subExchange ?? b.exchange}-${b.displayName}`))
            .map((x) => ({
                label: formattingService.toDisplayName(x),
                value: x,
            }));
    }, [translations, symbols, valueMatrixTableInputs.market]);

    useEffect(() => {
        const currentProduct = productOptions.find((x) => x.value.commodity === valueMatrixTableInputs.product.commodity)?.value;

        if (productOptions.length === 0 || currentProduct) {
            return;
        }

        // Whenever the productOptions change (after re-evaluation from market changing), we will need to reset the product.
        dispatch(
            setValueMatrixTableInputsRequest({
                ...valueMatrixTableInputs,
                product: productOptions[0].value,
                updateTriggeredBy: 'productChange',
            }),
        );
        props.refreshGetValueMatrixCalculator();
    }, [productOptions]);

    return (
        <div className={styles.value_matrix_dropdown}>
            <Dropdown
                testId="ValueMatrixProductDropdown"
                value={
                    productOptions.find(
                        (x) => x.value.commodity === valueMatrixTableInputs.product.commodity && x.value.region === valueMatrixTableInputs.product.region,
                    )?.value
                }
                options={productOptions}
                handleOptionChange={(value) => {
                    props.refreshGetValueMatrixCalculator();
                    dispatch(
                        setValueMatrixTableInputsRequest({
                            ...valueMatrixTableInputs,
                            product: value,
                            updateTriggeredBy: 'productChange',
                        }),
                    );
                }}
                label={translations.words.product}
                track={{ eventCategory: 'ValueMatrixCalculator', eventAction: 'ProductChanged' }}
            />
        </div>
    );
};

export default ValueMatrixProductDropdown;
